// Registry for hosted addons marketing pages (/addons). Mirrors the
// codex-infinity addon control plane (types, plans, env-var injection) with
// app.nz naming, plus gobed as the first-party GPU vector search flagship.

export interface AddonEnvVar {
  name: string;
  description: string;
  secret?: boolean;
}

export interface AddonPlan {
  plan: string;
  credits: string;
  detail: string;
}

export interface AddonEntry {
  slug: string;
  name: string;
  eyebrow: string;
  firstParty?: boolean;
  featured?: boolean;
  tagline: string;
  description: string;
  envVars: AddonEnvVar[];
  plans?: AddonPlan[];
  href: string;
}

export const ADDONS: AddonEntry[] = [
  {
    slug: 'gobed',
    name: 'gobed',
    eyebrow: 'GPU vector search · first party',
    firstParty: true,
    featured: true,
    tagline: 'GPU-accelerated semantic search with CAGRA graph indexes and int8 static embeddings.',
    description:
      'A Go vector engine with a 15 MB int8 embedding model, flat/IVF/HNSW indexes on CPU, and cuVS CAGRA graph search on GPU — the engine behind character, art, and repo search on app.nz.',
    envVars: [
      { name: 'GOBED_SEARCH_URL', description: 'HTTP endpoint of your provisioned gobed instance (POST /search, /batch_search, /index)' },
      { name: 'GOBED_API_KEY', description: 'Bearer token scoped to this addon', secret: true },
      { name: 'GOBED_INDEX', description: 'Default index/namespace name for this app' },
    ],
    plans: [
      { plan: 'Shared CPU', credits: 'metered', detail: 'int8 CPU engine on shared workers, per-request billing' },
      { plan: 'Dedicated CPU', credits: '40 credits/mo', detail: 'pinned instance, ~600 bytes/vector, up to ~5M vectors' },
      { plan: 'GPU (CAGRA)', credits: 'from $0.228/hr', detail: 'per-second billed GPU pod with cuVS CAGRA graph index' },
    ],
    href: '/addons/gobed',
  },
  {
    slug: 'postgres',
    name: 'Postgres',
    eyebrow: 'managed database · featured',
    featured: true,
    tagline: 'PostgreSQL hosting with HNSW vector search, PostGIS, graph traversal, and hybrid retrieval.',
    description:
      'Managed PostgreSQL with pgvector (HNSW) and PostGIS enabled by default, recursive-CTE graph traversal, full-text search, and hybrid retrieval — attached to your app as DATABASE_URL.',
    envVars: [
      { name: 'APP_ADDON_POSTGRES_URL', description: 'Full connection string (postgres://…)', secret: true },
      { name: 'DATABASE_URL', description: 'Set to the same URL when created with --attach-default', secret: true },
      { name: 'APP_ADDON_POSTGRES_HOST', description: 'Hostname' },
      { name: 'APP_ADDON_POSTGRES_PORT', description: 'Port' },
      { name: 'APP_ADDON_POSTGRES_DB', description: 'Database name' },
      { name: 'APP_ADDON_POSTGRES_USER', description: 'Role name' },
      { name: 'APP_ADDON_POSTGRES_PASSWORD', description: 'Password', secret: true },
    ],
    plans: [
      { plan: 'Starter', credits: '50 credits/mo', detail: 'shared pool, daily backups' },
      { plan: 'Pro', credits: '150 credits/mo', detail: 'more compute + storage, PITR backups' },
      { plan: 'Scale', credits: '400 credits/mo', detail: 'dedicated resources, read replicas on request' },
    ],
    href: '/addons/postgres',
  },
  {
    slug: 'mongo',
    name: 'Mongo',
    eyebrow: 'document database',
    tagline: 'Managed MongoDB for document workloads.',
    description: 'Managed MongoDB with the same one-command attach, env injection, backups, and credit billing.',
    envVars: [
      { name: 'APP_ADDON_MONGO_URL', description: 'Connection string', secret: true },
      { name: 'APP_ADDON_MONGO_HOST', description: 'Hostname' },
      { name: 'APP_ADDON_MONGO_DB', description: 'Database name' },
      { name: 'APP_ADDON_MONGO_PASSWORD', description: 'Password', secret: true },
    ],
    plans: [
      { plan: 'Starter', credits: '60 credits/mo', detail: 'shared pool' },
      { plan: 'Pro', credits: '170 credits/mo', detail: 'more compute + storage' },
      { plan: 'Scale', credits: '420 credits/mo', detail: 'dedicated resources' },
    ],
    href: '/addons#mongo',
  },
  {
    slug: 'memcache',
    name: 'Memcache',
    eyebrow: 'cache',
    tagline: 'Shared memcache namespace for hot keys and sessions.',
    description: 'A shared memcache instance with a per-app namespace for pages, sessions, and expensive lookups.',
    envVars: [
      { name: 'APP_ADDON_MEMCACHE_URL', description: 'Endpoint URL' },
      { name: 'APP_ADDON_MEMCACHE_NAMESPACE', description: 'Key prefix isolated to this app' },
    ],
    href: '/addons#memcache',
  },
  {
    slug: 'auth',
    name: 'Auth',
    eyebrow: 'auth + realtime',
    tagline: 'Supabase-style auth and realtime channels.',
    description: 'Hosted auth (email, OAuth, JWTs) plus realtime pub/sub channels, with anon and service keys injected into your environment.',
    envVars: [
      { name: 'APP_ADDON_AUTH_URL', description: 'Auth API base URL' },
      { name: 'APP_ADDON_REALTIME_URL', description: 'Realtime channels endpoint' },
      { name: 'APP_ADDON_AUTH_ANON_KEY', description: 'Public client key' },
      { name: 'APP_ADDON_AUTH_SERVICE_KEY', description: 'Server-side key', secret: true },
      { name: 'APP_ADDON_AUTH_JWT_SECRET', description: 'Token signing secret', secret: true },
    ],
    href: '/addons#auth',
  },
  {
    slug: 'monitor',
    name: 'Monitor Agent',
    eyebrow: 'AI ops',
    tagline: 'AI-assisted error intake, dedupe, and auto-tasking.',
    description: 'Point your app’s errors at the ingest endpoint; the monitor agent dedupes, triages, and can open repo tasks for real regressions.',
    envVars: [
      { name: 'APP_ADDON_MONITOR_INGEST_URL', description: 'Error/event ingest endpoint' },
      { name: 'APP_ADDON_MONITOR_INGEST_KEY', description: 'Ingest key', secret: true },
      { name: 'APP_ADDON_MONITOR_DASHBOARD_URL', description: 'Dashboard for this project' },
    ],
    plans: [
      { plan: 'Starter', credits: '15 credits/mo', detail: 'error intake + dedupe' },
      { plan: 'Pro', credits: '40 credits/mo', detail: 'auto-tasking into repos' },
      { plan: 'Scale', credits: '120 credits/mo', detail: 'high volume + SLOs' },
    ],
    href: '/addons#monitor',
  },
];

export const addonBySlug: Record<string, AddonEntry> = Object.fromEntries(ADDONS.map((a) => [a.slug, a]));
